import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    LabelList,
} from 'recharts';
import type { DashboardFiltros } from './types';
import type { IDashboardConciliacao } from '../../interfaces/IDashboard';
import DashboardService from '../../services/DashboardService';

type StatusCardRow = { coluna: string; [status: string]: string | number };

type Props = {
    filtros: DashboardFiltros;
};

const cores = {
    pendentes: '#ef3b2d',
    conciliacaoAutomatica: '#79b84d',
    conciliacaoParcial: '#f4c542',
    emConferencia: '#4f8f46',
    concluido: '#226b3a',
    arquivado: '#9aa0a6',
    verdePrincipal: '#178b57',
    cinzaBorda: '#e9ecef',
    fundoCard: '#ffffff',
    cinzaTexto: '#5f6368',
};

const STATUS_INFO: Record<string, { label: string; color: string }> = {
    Pendentes: { label: 'Pendentes', color: cores.pendentes },
    'Em processamento': { label: 'Conciliação Automática', color: cores.conciliacaoAutomatica },
    'Conciliado Parcial': { label: 'Conciliação Parcial', color: cores.conciliacaoParcial },
    'Em Conferencia': { label: 'Em Conferência', color: cores.emConferencia },
    Concluida: { label: 'Concluído', color: cores.concluido },
    Arquivado: { label: 'Arquivado', color: cores.arquivado },
};

const PALETA_FALLBACK = ['#8950fc', '#3699ff', '#ffa800', '#f64e60', '#1bc5bd'];

const corParaStatus = (status: string, indice: number) =>
    STATUS_INFO[status]?.color ?? PALETA_FALLBACK[indice % PALETA_FALLBACK.length];

const rotuloParaStatus = (status: string) => STATUS_INFO[status]?.label ?? status;

/**
 * Mostra um único número (o total da barra empilhada) logo após o fim de todos os segmentos —
 * evita sobreposição de rótulos quando a categoria tem vários status (Pendente + Parcial + Em
 * Conferência + Concluído etc.), já que cada linha só ganha uma etiqueta, não uma por segmento.
 */
const rotuloTotalEmpilhado = (chaves: string[], linhas: any[]) => (props: any) => {
    const x = Number(props?.x ?? 0);
    const y = Number(props?.y ?? 0);
    const width = Number(props?.width ?? 0);
    const height = Number(props?.height ?? 0);
    const index = props?.index;
    const linha = index !== undefined ? linhas[index] : undefined;
    if (!linha) return null;

    const total = chaves.reduce((acc, chave) => acc + (Number(linha[chave]) || 0), 0);
    if (!total) return null;

    return (
        <text x={x + width + 6} y={y + height / 2} dy={4} fontSize={12} fontWeight={700} fill='#3c4043'>
            {total}
        </text>
    );
};

const DashboardConciliacao = ({ filtros }: Props) => {
    const [dados, setDados] = useState<IDashboardConciliacao | null>(null);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (!filtros.empresaSelecionada) return;

        let ativo = true;

        async function carregar() {
            setCarregando(true);
            setErro(null);

            try {
                const resultado = await DashboardService.obterConciliacao(filtros.empresaSelecionada, filtros);

                if (ativo) setDados(resultado);
            } catch (e: any) {
                if (ativo) setErro(e?.message ?? 'Erro ao carregar o dashboard');
            } finally {
                if (ativo) setCarregando(false);
            }
        }

        carregar();

        return () => {
            ativo = false;
        };
    }, [
        filtros.empresaSelecionada,
        filtros.tipoPeriodo,
        filtros.anoSelecionado,
        filtros.mesSelecionado,
        filtros.diaSelecionado,
        filtros.dataInicial,
        filtros.dataFinal,
    ]);

    const totalPie = useMemo(
        () => dados?.statusContas.reduce((acc, item) => acc + item.valor, 0) ?? 0,
        [dados]
    );

    const pieData = useMemo(() => {
        if (!dados) return [];

        return dados.statusContas.map((item, indice) => ({
            name: rotuloParaStatus(item.status),
            value: item.valor,
            color: corParaStatus(item.status, indice),
            percentual: totalPie > 0 ? Math.round((item.valor / totalPie) * 100) : 0,
        }));
    }, [dados, totalPie]);

    const statusesPresentes = useMemo(() => {
        if (!dados) return [];
        return Array.from(new Set(dados.statusCards.map((item) => item.status)));
    }, [dados]);

    const statusCardsRows = useMemo<StatusCardRow[]>(() => {
        if (!dados) return [];

        const porCategoria = new Map<string, StatusCardRow>();

        dados.statusCards.forEach((item) => {
            if (!porCategoria.has(item.categoria)) {
                porCategoria.set(item.categoria, { coluna: item.categoria });
            }
            porCategoria.get(item.categoria)![item.status] = item.quantidade;
        });

        return Array.from(porCategoria.values());
    }, [dados]);

    const cardStyle: CSSProperties = {
        background: cores.fundoCard,
        border: `1px solid ${cores.cinzaBorda}`,
        borderRadius: '14px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        padding: '14px 16px',
        height: '100%',
    };

    const tituloCard: CSSProperties = {
        fontSize: '18px',
        fontWeight: 700,
        color: '#3c4043',
        marginBottom: '18px',
    };

    const subtitulo: CSSProperties = {
        fontSize: '14px',
        fontWeight: 700,
        color: '#3c4043',
        marginBottom: '12px',
    };

    const legendaStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        fontSize: '14px',
        color: '#4b4f52',
    };

    if (carregando) {
        return (
            <div className='row g-5'>
                <div className='col-12'>
                    <div style={cardStyle}>
                        <span style={{ color: cores.cinzaTexto }}>Carregando dashboard...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (erro) {
        return (
            <div className='row g-5'>
                <div className='col-12'>
                    <div style={cardStyle}>
                        <span style={{ color: cores.pendentes }}>{erro}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='row g-5'>
            <div className='col-sm-12 col-xl-6'>
                <div style={cardStyle}>
                    <div style={tituloCard}>Visão Geral - Status de Conciliação</div>

                    <div style={subtitulo}>Status das Contas</div>

                    <div className='row align-items-center mb-8'>
                        <div className='col-md-6' style={{ height: 220 }}>
                            <ResponsiveContainer width='100%' height='100%'>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx='50%'
                                        cy='50%'
                                        innerRadius={58}
                                        outerRadius={95}
                                        dataKey='value'
                                        paddingAngle={0}
                                        labelLine={false}
                                        label={({ percentual }) => `${percentual}%`}
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className='col-md-6'>
                            {pieData.map((item, index) => (
                                <div key={`legend-${index}`} style={legendaStyle}>
                                    <span
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 2,
                                            backgroundColor: item.color,
                                            display: 'inline-block',
                                            marginRight: 10,
                                        }}
                                    />
                                    <span>{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div
                        style={{
                            borderTop: '1px solid #ececec',
                            paddingTop: '12px',
                        }}
                    >
                        <div style={subtitulo}>Status dos Cards</div>

                        <div style={{ width: '100%', height: Math.max(250, statusCardsRows.length * 34) }}>
                            <ResponsiveContainer width='100%' height='100%'>
                                <BarChart
                                    data={statusCardsRows}
                                    layout='vertical'
                                    margin={{ top: 0, right: 35, left: 5, bottom: 0 }}
                                    barCategoryGap={14}
                                >
                                    <XAxis type='number' hide />
                                    <YAxis
                                        type='category'
                                        dataKey='coluna'
                                        width={95}
                                        interval={0}
                                        tick={{ fontSize: 12, fill: '#4b4f52' }}
                                    />
                                    <Tooltip />
                                    {statusesPresentes.map((status, indice) => (
                                        <Bar
                                            key={status}
                                            dataKey={status}
                                            stackId='a'
                                            fill={corParaStatus(status, indice)}
                                        >
                                            {indice === statusesPresentes.length - 1 && (
                                                <LabelList
                                                    dataKey={status}
                                                    content={rotuloTotalEmpilhado(statusesPresentes, statusCardsRows)}
                                                />
                                            )}
                                        </Bar>
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '12px',
                                fontSize: '12px',
                                marginTop: '10px',
                                color: '#4b4f52',
                            }}
                        >
                            {statusesPresentes.map((status, indice) => (
                                <div key={status} style={{ display: 'flex', alignItems: 'center' }}>
                                    <span
                                        style={{
                                            width: 10,
                                            height: 10,
                                            background: corParaStatus(status, indice),
                                            display: 'inline-block',
                                            marginRight: 6,
                                            borderRadius: 2,
                                        }}
                                    />
                                    {rotuloParaStatus(status)}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className='col-sm-12 col-xl-6'>
                <div style={cardStyle}>
                    <div style={tituloCard}>Análise de Pendências</div>

                    <div style={subtitulo}>Total de Ajustes e Pendências</div>

                    <div style={{ width: '100%', height: Math.max(280, (dados?.totalAjustesPendencias.length ?? 0) * 26) }}>
                        <ResponsiveContainer width='100%' height='100%'>
                            <BarChart
                                data={dados?.totalAjustesPendencias ?? []}
                                layout='vertical'
                                margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
                                barCategoryGap={10}
                            >
                                <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke='#f1f3f4' />
                                <XAxis type='number' hide />
                                <YAxis
                                    type='category'
                                    dataKey='categoria'
                                    width={110}
                                    interval={0}
                                    tick={{ fontSize: 11, fill: '#4b4f52' }}
                                />
                                <Tooltip />
                                <Bar dataKey='ate30Dias' stackId='b' fill={cores.emConferencia} />
                                <Bar dataKey='ate60Dias' stackId='b' fill={cores.conciliacaoParcial} />
                                <Bar dataKey='ate90Dias' stackId='b' fill='#f7931e' />
                                <Bar dataKey='acima90Dias' stackId='b' fill={cores.pendentes} radius={[0, 4, 4, 0]}>
                                    <LabelList
                                        dataKey='acima90Dias'
                                        content={rotuloTotalEmpilhado(
                                            ['ate30Dias', 'ate60Dias', 'ate90Dias', 'acima90Dias'],
                                            dados?.totalAjustesPendencias ?? []
                                        )}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div
                        style={{
                            marginTop: '8px',
                            border: '1px solid #ececec',
                            borderRadius: '10px',
                            background: '#fafafa',
                            padding: '16px',
                        }}
                    >
                        <div style={subtitulo}>Age de Pendências por Usuário</div>

                        {(dados?.agePendenciasPorUsuario.length ?? 0) === 0 ? (
                            <span style={{ color: cores.cinzaTexto, fontSize: '13px' }}>
                                Nenhum cartão desta empresa/período tem um responsável definido ainda —
                                mova um cartão no Kanban para começar a vincular um usuário às pendências.
                            </span>
                        ) : (
                            <div style={{ width: '100%', height: Math.max(170, (dados?.agePendenciasPorUsuario.length ?? 0) * 34) }}>
                                <ResponsiveContainer width='100%' height='100%'>
                                    <BarChart
                                        data={dados?.agePendenciasPorUsuario ?? []}
                                        layout='vertical'
                                        margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
                                        barCategoryGap={14}
                                    >
                                        <XAxis type='number' hide />
                                        <YAxis
                                            type='category'
                                            dataKey='usuario'
                                            width={85}
                                            interval={0}
                                            tick={{ fontSize: 12, fill: '#4b4f52' }}
                                        />
                                        <Tooltip />
                                        <Bar dataKey='ate30Dias' stackId='c' fill={cores.emConferencia} />
                                        <Bar dataKey='ate60Dias' stackId='c' fill={cores.conciliacaoParcial} />
                                        <Bar dataKey='ate90Dias' stackId='c' fill='#f7931e' />
                                        <Bar dataKey='acima90Dias' stackId='c' fill={cores.pendentes}>
                                            <LabelList
                                                dataKey='acima90Dias'
                                                content={rotuloTotalEmpilhado(
                                                    ['ate30Dias', 'ate60Dias', 'ate90Dias', 'acima90Dias'],
                                                    dados?.agePendenciasPorUsuario ?? []
                                                )}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px',
                            fontSize: '12px',
                            marginTop: '12px',
                            color: '#4b4f52',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: 10, height: 10, background: cores.emConferencia, display: 'inline-block', marginRight: 6, borderRadius: 2 }} />
                            Até 30 dias
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: 10, height: 10, background: cores.conciliacaoParcial, display: 'inline-block', marginRight: 6, borderRadius: 2 }} />
                            De 31 a 60 dias
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: 10, height: 10, background: '#f7931e', display: 'inline-block', marginRight: 6, borderRadius: 2 }} />
                            De 61 a 90 dias
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: 10, height: 10, background: cores.pendentes, display: 'inline-block', marginRight: 6, borderRadius: 2 }} />
                            Acima de 90 dias
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardConciliacao;
